import _ from "lodash";
import { flow, map, filter, uniq, uniqBy, compact } from "lodash/fp";
import ko from "knockout";
import Promise from "bluebird";
import ViewModel from "viewModels/ViewModel";
import AgentSelectorView from "viewModels/policies/editors/AgentSelectorView";
import EvaluationFormSelectorView from "viewModels/policies/editors/EvaluationFormSelectorView";
import ServiceResponseValidator from "viewModels/util/ServiceResponseValidator";
import qualityAuthorization from "services/qualityAuth";
import qualityService from "services/qualityManagementService";
import userService from "services/userService";
import authService from "services/authService";
import queueService from "services/queuesService";
import toastService from "services/toastService";
import errorCallbackFactory from "util/errorCallbackFactory";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";

export const EVALUATOR_TYPES = {
    AUTO_SUBMIT: "autoSubmit",
    ASSIGN_TO_EVALUATOR: "assignToEvaluator",
};

const EVALUATION_FORM_TYPES = {
    AUTOMATED: "Automated",
};

export const QUALITY_RESPONSE_CODES = {
    MISSING_PARTICIPATE_PERMISSION:
        "quality.evaluation.agent.doesnt.have.participate.permission",
    CREATE_LIMIT_EXCEEDED: "quality.evaluation.create.limit.exceeded.for.agent",
    ALREADY_IN_PROGRESS: "quality.evaluation.already.in.progress",
};

const CreateEvaluationView = ViewModel.extend(function (
    conversationObservable,
    newEvaluationCallback,
    cancelCreateEvaluationCallback,
    createCalibrationCallback,
    useAsCalibration = false,
    internalUsers,
    conversationIdValue
) {
    const self = this;
    const isSparkFlareFTEnabled = featureToggleService.isEnabled(
        FeatureToggle.WEM_1418_IDUI_SPARK_V4_FLARE
    );
    self.selectedForm = ko.observable(null);
    self.expertEvaluator = ko.observable(null);
    self.evaluationView = ko.observable();
    let conversationId;
    self.saving = ko.observable(false);
    self.loading = ko.observable(true);
    self.loadingEvaluationForm = ko.observable(false);
    // Make EvaluatorType available to the template
    self.EvaluatorType = EVALUATOR_TYPES;
    self.useAsCalibrationRadioGroupValue = ko.observable(useAsCalibration);
    self.isExpertEvaluation = ko.observable(false);
    self.agentOnInteraction = ko.observable();
    self.queueOnInteraction = ko.observable();
    self.internalAgents = ko.observableArray([]);
    self.queuesForConversation = ko.observableArray();
    self.canAddCalibration = qualityAuthorization.canAddCalibration();
    self.isAutomatedEvaluationFeatureEnabled = ko.computed(function () {
        return (
            featureToggleService.isEnabled(
                FeatureToggle.AUTOMATED_EVALUATION_COMPLETION
            ) &&
            featureToggleService.isEnabled(
                FeatureToggle.EVALUATION_FORM_DEFAULT_ANSWERING
            )
        );
    });
    self.shouldShowAiScoringInfoLabel = ko.computed(function () {
        return (
            self.isAutomatedEvaluationFeatureEnabled() &&
            !self.useAsCalibrationRadioGroupValue()
        );
    });
    self.hasSelectedForm = self.computed(function () {
        return self.selectedForm() !== null;
    });
    self.showEvaluatorSelector = self.computed(function () {
        return (
            !self.isAutomatedEvaluationFeatureEnabled() ||
            (self.hasSelectedForm() &&
                self.evaluatorType() === EVALUATOR_TYPES.ASSIGN_TO_EVALUATOR)
        );
    });
    self.showFooter = self.computed(function () {
        return (
            !self.isAutomatedEvaluationFeatureEnabled() ||
            self.hasSelectedForm() ||
            self.useAsCalibrationRadioGroupValue()
        );
    });

    self.missingParticipatePermission = ko.observable(null);
    const responseValidator = new ServiceResponseValidator();
    self.selectedEvaluator = ko
        .observable(null)
        .extend({ responseValidator: responseValidator });
    self.calibrationEvaluators = ko
        .observableArray([])
        .extend({ responseValidator: responseValidator });

    if (self.isAutomatedEvaluationFeatureEnabled()) {
        self.evaluatorType = ko.observable(EVALUATOR_TYPES.AUTO_SUBMIT); // Default to auto-submit for Virtual Supervisor
        self.isAutomatedFormSelected = ko.observable(false);

        // Reset evaluator validation when switching evaluator type
        self.subscribe(self.evaluatorType, function (newType) {
            if (newType === EVALUATOR_TYPES.AUTO_SUBMIT) {
                self.selectedEvaluator(null);
                self.selectedEvaluator.isModified(false);
            }
        });
    }
    self.showEvaluatorTypeSection = self.computed(function () {
        return (
            self.isAutomatedEvaluationFeatureEnabled() &&
            self.isAutomatedFormSelected()
        );
    });
    self.showEvaluatorSelectorValidation = self.computed(function () {
        return (
            (!self.isAutomatedEvaluationFeatureEnabled() ||
                self.evaluatorType() === EVALUATOR_TYPES.ASSIGN_TO_EVALUATOR) &&
            !self.useAsCalibrationRadioGroupValue()
        );
    });

    self.subscribe(self.useAsCalibrationRadioGroupValue, function (newVal) {
        // Reset values and validation for evaluators when the calibration radio is changed.
        if (newVal) {
            self.selectedEvaluator(null);
            self.selectedEvaluator.isModified(false);
            self.missingParticipatePermission(null);
        } else {
            self.calibrationEvaluators([]);
            self.calibrationEvaluators.isModified(false);
            self.missingParticipatePermission(null);
        }
    });

    let queueAndUser;
    const buildQueueAndUser = function (participants) {
        function mapUserIdToQueues(userId) {
            return _.compact(
                _.map(_.filter(participants, { userId: userId }), "queueId")
            );
        }

        function expandQueues() {
            const queueIds = flow(map("queueId"), compact, uniq)(participants);
            return queueService
                .getQueuesBrief({ pageSize: 100, id: queueIds })
                .then(function (response) {
                    const briefQueues = response.entities;
                    const briefQueueIds = _.map(briefQueues, function (queue) {
                        return queue.id;
                    });
                    const missingQueueIds = _.difference(
                        queueIds,
                        briefQueueIds
                    );
                    const results = _.map(briefQueues, function (briefQueue) {
                        return {
                            id: briefQueue.id,
                            name: briefQueue.name,
                        };
                    });
                    return _.union(
                        results,
                        _.map(missingQueueIds, function (missingQueueId) {
                            return {
                                id: missingQueueId,
                                name: ko.i18n.renderString(
                                    "evaluation.noQueueName"
                                ),
                            };
                        })
                    );
                });
        }

        function expandUser(participant) {
            return userService
                .getUser({ userId: participant.userId })
                .then(function (result) {
                    return {
                        id: result.id,
                        displayName: result.name,
                    };
                })
                .catch(function (e) {
                    errorCallbackFactory.createCallback({
                        namespace: "evaluation.userOrQueueError",
                    })(e);
                    return {
                        id: participant.userId,
                        displayName: ko.i18n.renderString(
                            "evaluation.noAgentName"
                        ),
                    };
                });
        }

        return {
            /**
             * gets all of the queues and returns them
             * @returns {Array}
             */
            getQueues() {
                //todo: apiFix so that we don't always have to expand queues
                return expandQueues();
            },

            /**
             * gets the users and if necessary, expands userIds
             * @returns {Promise}
             */
            getUsers() {
                return Promise.all(
                    flow(
                        filter(function (participant) {
                            const isDigitalConsultEnabled =
                                featureToggleService.isEnabled(
                                    FeatureToggle.PURE_5806_DIGITAL_CONSULT
                                );
                            const isWEM437Enabled =
                                featureToggleService.isEnabled(
                                    FeatureToggle.WEM_437_SCREEN_MONITORING
                                );
                            /* 
                            In Message Conversation, Participant is said to be consultant if:
                            the participant has atleast one internal message and no regular messages
                            But if there are both messages and internal message then:
                            either it will be agent who initiated consult or agent transfered call to consultant
                            */
                            const isMessageConsultant =
                                participant.internalMessages?.length > 0 &&
                                !participant.messages?.length > 0;

                            const isScreenMonitoringSupervisor =
                                isWEM437Enabled &&
                                participant.screenMonitorings != null &&
                                Array.isArray(participant.screenMonitorings) &&
                                participant.screenMonitorings.length > 0;

                            return (
                                participant.purpose &&
                                (participant.purpose.toLowerCase() === "user" ||
                                    participant.purpose.toLowerCase() ===
                                        "agent") &&
                                !participant.monitoredParticipantId &&
                                !participant.coachedParticipantId &&
                                !(
                                    participant.mediaRoles &&
                                    participant.mediaRoles.includes("barge")
                                ) &&
                                (isDigitalConsultEnabled
                                    ? !isMessageConsultant
                                    : true) &&
                                !isScreenMonitoringSupervisor
                            );
                        }),
                        uniqBy("userId"),
                        map(function (participant) {
                            if (
                                !participant.userId &&
                                !_.isEmpty(participant.name)
                            ) {
                                return Promise.resolve().then(function () {
                                    return {
                                        id: participant.userId,
                                        displayName: participant.name,
                                    };
                                });
                            } else {
                                return expandUser(participant);
                            }
                        }),
                        compact
                    )(participants)
                );
            },

            findCorrectQueues(userId, queues) {
                const queueIds = mapUserIdToQueues(userId);
                return _.filter(queues, (queue) =>
                    _.includes(queueIds, queue.id)
                );
            },
        };
    };

    self.subscribe(self.internalAgents, function () {
        if (isSparkFlareFTEnabled) {
            const agents = self.internalAgents();
            if (agents && agents.length > 0) {
                self.agentOnInteraction(agents[0]);
                if (agents.length === 1) {
                    self.missingParticipatePermission(null);
                }
            }
        } else if (
            self.internalAgents() &&
            self.internalAgents().length === 1
        ) {
            self.agentOnInteraction(self.internalAgents()[0]);
            self.missingParticipatePermission(null);
        }
    });

    self.computed(function () {
        const conversation = ko.toJS(conversationObservable);
        if (conversation && conversation.id) {
            queueAndUser = buildQueueAndUser(conversation.participants);
            conversationId = conversation.id;

            const usersPromise = queueAndUser
                .getUsers()
                .then(function (result) {
                    if (!_.isEmpty(result)) {
                        //compare the users from result set and update the observable only when there is difference
                        const hasAgentsDifference =
                            _.differenceBy(result, self.internalAgents(), "id")
                                .length > 0;
                        if (
                            _.isEmpty(self.internalAgents()) ||
                            hasAgentsDifference
                        ) {
                            self.internalAgents(_.uniq(result, "id"));
                        }
                    }
                });

            const queuesPromise = queueAndUser
                .getQueues()
                .then(function (values) {
                    if (!_.isEmpty(values)) {
                        //compare the queue from result set and update the observable only when there is difference
                        const hasQueueDifference =
                            _.differenceBy(
                                values,
                                self.queuesForConversation(),
                                "id"
                            ).length > 0;
                        if (
                            _.isEmpty(self.queuesForConversation()) ||
                            hasQueueDifference
                        ) {
                            self.queuesForConversation(values);
                        }
                    }
                });

            Promise.all([usersPromise, queuesPromise])
                .catch(
                    errorCallbackFactory.createCallback({
                        namespace: "evaluation.userOrQueueError",
                    })
                )
                .finally(function () {
                    self.loading(false);
                });
        } else if (conversation && !conversation.id) {
            conversationId = conversationIdValue;
            self.loading(false);
            self.internalAgents(_.uniq(internalUsers(), "id"));
        }
    });

    self.queuesForAgent = self.computed(function () {
        if (self.agentOnInteraction() && queueAndUser) {
            return queueAndUser.findCorrectQueues(
                self.agentOnInteraction().id,
                self.queuesForConversation()
            );
        } else {
            return self.queuesForConversation();
        }
    });

    //helper function to find a queue in the queuesForAgent array
    const findQueueInQueuesForAgent = function (id) {
        if (!id) {
            return null;
        }
        return self.queuesForAgent()?.find((queue) => queue?.id === id);
    };

    self.changeQueueOnInteraction = function (_data, event) {
        self.queueOnInteraction(
            findQueueInQueuesForAgent(event?.target?.value)
        );
    };

    self.subscribe(self.queuesForAgent, function () {
        if (self.queuesForAgent().length === 0) {
            self.queueOnInteraction(null);
            return;
        }

        // if the selected queue is not in the queuesForAgent array, set it to the first queue
        const selectedQueueId = self.queueOnInteraction()?.id;
        const selectedQueueExists =
            !!findQueueInQueuesForAgent(selectedQueueId);
        if (selectedQueueExists) {
            return;
        }
        self.queueOnInteraction(self.queuesForAgent()[0]);
    });

    self.evaluationFormSelector = self.subView(
        EvaluationFormSelectorView,
        self.selectedForm,
        useAsCalibration
    );

    self.evaluatorSelector = self.subView(
        AgentSelectorView,
        self.selectedEvaluator
    );
    self.calibrationEvaluatorsSelector = self.subView(
        AgentSelectorView,
        self.calibrationEvaluators
    );

    if (!isSparkFlareFTEnabled) {
        self.expertEvaluatorSelector = self.subView(
            AgentSelectorView,
            self.expertEvaluator
        );
    }

    self.hasMultipleAgents = self.computed(function () {
        return self.internalAgents() && self.internalAgents().length > 1;
    });

    //convenience method for single agents
    self.hasSingleAgent = self.computed(function () {
        return (
            self.internalAgents() &&
            self.internalAgents().length === 1 &&
            self.agentOnInteraction() &&
            self.agentOnInteraction().displayName
        );
    });

    self.hasMultipleQueues = self.computed(function () {
        return (
            self.queuesForConversation() &&
            self.queuesForConversation().length > 1
        );
    });

    self.hasSingleQueue = self.computed(function () {
        return (
            self.queuesForConversation() &&
            self.queuesForConversation().length === 1 &&
            self.queueOnInteraction() &&
            self.queueOnInteraction().name
        );
    });

    self.expertEvaluationChanged = function () {
        self.isExpertEvaluation(!self.isExpertEvaluation());
    };

    const findExpertEvaluatorInCalibrationEvaluators = function (id) {
        if (!id) {
            return null;
        }
        return self
            .calibrationEvaluators()
            ?.find((evaluator) => evaluator?.id === id);
    };

    self.changeExpertEvaluator = function (_data, event) {
        const evaluator = findExpertEvaluatorInCalibrationEvaluators(
            event?.target?.value
        );
        self.expertEvaluator(evaluator);
    };

    self.subscribe(self.calibrationEvaluators, function () {
        if (self.calibrationEvaluators().length === 0) {
            self.expertEvaluator(null);
            return;
        }

        // if the selected expert evaluator is not in the calibrationEvaluators, set it to the first evaluator
        const selectedExpertEvaluatorId = self.expertEvaluator()?.id;
        const selectedExpertEvaluatorExists =
            !!findExpertEvaluatorInCalibrationEvaluators(
                selectedExpertEvaluatorId
            );
        if (selectedExpertEvaluatorExists) {
            return;
        }
        self.expertEvaluator(self.calibrationEvaluators()[0]);
    });

    self.save = function () {
        const selectedForm = self.selectedForm();
        const selectedEvaluator = self.selectedEvaluator();
        const selectedAgent = self.agentOnInteraction();
        const selectedQueue = self.queueOnInteraction();
        const expertEvaluator = self.expertEvaluator();
        const useAsCalibration = ko.unwrap(
            self.useAsCalibrationRadioGroupValue
        );
        const isExpertEvaluation = self.isExpertEvaluation();
        if (self.internalAgents()) {
            if (useAsCalibration) {
                const calibration = {
                    calibrator: { id: authService.principalId() },
                    conversation: { id: conversationId },
                    agent: { id: selectedAgent.id },
                };

                const calibrationEvaluators = ko.unwrap(
                    self.calibrationEvaluators
                );
                calibration.evaluators = _.map(
                    calibrationEvaluators,
                    function (evaluator) {
                        return {
                            id: evaluator.id,
                        };
                    }
                );

                calibration.evaluationForm = selectedForm;
                if (isExpertEvaluation) {
                    calibration.expertEvaluator = {
                        id: expertEvaluator.id,
                    };
                }

                self.saving(true);
                return qualityService
                    .createCalibration(calibration)
                    .then(function (response) {
                        responseValidator.handleSuccess();
                        createCalibrationCallback(response);
                    })
                    .catch(function (err) {
                        if (
                            err &&
                            err.details &&
                            err.details.response &&
                            err.details.response.code ===
                                QUALITY_RESPONSE_CODES.MISSING_PARTICIPATE_PERMISSION
                        ) {
                            return self.handleMissingPartcipatePermission();
                        } else {
                            return responseValidator.createErrorCallback({
                                namespace:
                                    "calibration.createCalibrationRequest",
                            })(err);
                        }
                    })
                    .finally(function () {
                        self.saving(false);
                    });
            } else {
                const evaluation = {
                    evaluationForm: selectedForm,
                    conversation: { id: conversationId },
                };

                // don't include evaluator if auto-submit
                if (
                    !self.isAutomatedEvaluationFeatureEnabled() ||
                    self.evaluatorType() === EVALUATOR_TYPES.ASSIGN_TO_EVALUATOR
                ) {
                    evaluation.evaluator = { id: selectedEvaluator.id };
                }

                if (selectedAgent && selectedAgent.id) {
                    evaluation.agent = { id: selectedAgent.id };
                }

                if (selectedQueue && selectedQueue.id) {
                    evaluation.queue = { id: selectedQueue.id };
                }

                self.saving(true);
                return qualityService
                    .createEvaluation(evaluation)
                    .then(function (response) {
                        responseValidator.handleSuccess();
                        // Only set evaluator in response for manual evaluator assignments
                        if (
                            !self.isAutomatedEvaluationFeatureEnabled() ||
                            self.evaluatorType() ===
                                EVALUATOR_TYPES.ASSIGN_TO_EVALUATOR
                        ) {
                            // evaluator name is used in PlaybackView for a notification;
                            // passing along evaluator this way to avoid another server lookup
                            response.evaluator = self.selectedEvaluator();
                            newEvaluationCallback(response);
                        } else {
                            // For auto-submit with Virtual Supervisor, close the create screen and show an in Progress notification
                            toastService.notifyMessage(
                                "evaluation.createEvaluationRequest.automatedEvaluationCreation.inProgress.title",
                                {
                                    bodyMessageKey:
                                        "evaluation.createEvaluationRequest.automatedEvaluationCreation.inProgress.body",
                                }
                            );
                            self.removeActiveTab();
                        }
                    })
                    .catch(function (err) {
                        if (
                            err &&
                            err.details &&
                            err.details.response &&
                            err.details.response.code ===
                                QUALITY_RESPONSE_CODES.MISSING_PARTICIPATE_PERMISSION
                        ) {
                            return self.handleMissingPartcipatePermission();
                        } else if (
                            err &&
                            err.details &&
                            err.details.response &&
                            err.details.response.code ===
                                QUALITY_RESPONSE_CODES.CREATE_LIMIT_EXCEEDED
                        ) {
                            toastService.notifyMajorAlert(
                                "evaluation.createEvaluationRequest.error.dailyLimitReached"
                            );
                        } else if (
                            (featureToggleService.isEnabled(
                                FeatureToggle.QM_19313_EVALUATION_DEDUPLICATION
                            ) &&
                                err?.details?.status === 409) ||
                            err?.details?.response?.code ===
                                QUALITY_RESPONSE_CODES.ALREADY_IN_PROGRESS
                        ) {
                            toastService.notifyMajorAlert(
                                "evaluation.createEvaluationRequest.error.alreadyInProgress.title",
                                {
                                    bodyMessageKey:
                                        "evaluation.createEvaluationRequest.error.alreadyInProgress.body",
                                }
                            );
                        } else {
                            return responseValidator.createErrorCallback({
                                namespace: "evaluation.createEvaluationRequest",
                            })(err);
                        }
                    })
                    .finally(function () {
                        self.saving(false);
                    });
            }
        } else {
            toastService.notifyMajorAlert(
                "playback.nav.evaluation.new.missing.agent"
            );
        }
    };

    self.computed(function () {
        if (self.evaluationView()) {
            self.evaluationView().dispose();
            self.evaluationView(null);
        }

        if (self.isAutomatedEvaluationFeatureEnabled()) {
            // Check if the selected form is an automated form
            const selectedForm = self.selectedForm();
            if (
                selectedForm &&
                selectedForm.evaluationFormType === "Automated"
            ) {
                self.isAutomatedFormSelected(true);
                self.evaluatorType(EVALUATOR_TYPES.AUTO_SUBMIT);
            } else {
                self.isAutomatedFormSelected(false);
                self.evaluatorType(EVALUATOR_TYPES.ASSIGN_TO_EVALUATOR);
            }
        }
    });

    self.removeActiveTab = function () {
        cancelCreateEvaluationCallback();
    };

    self.onDispose = function () {
        if (self.evaluationView()) {
            self.evaluationView().dispose();
            self.evaluationView(null);
        }
    };

    self.handleMissingPartcipatePermission = function () {
        const agentMissingParticipate = self.agentOnInteraction().displayName;
        let result = "";
        if (agentMissingParticipate) {
            result = ko.i18n.renderString(
                "evaluation.agentIDMissingParticipate",
                {
                    value: agentMissingParticipate,
                }
            );
        } else {
            result = ko.i18n.renderString("evaluation.agentMissingParticipate");
        }
        self.missingParticipatePermission(result);
    };

    self.resetError = function () {
        self.missingParticipatePermission(null);
    };
});

export default CreateEvaluationView;
